import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import "../../../styles/FacturaCrear.css";

/* ============================================================================
   CREAR FACTURA · cabecera del bloque de procedimientos

   Sustituye a los CUATRO BOTONES MORADOS IDÉNTICOS de la barra de título, en
   los que la acción principal no se distinguía de las otras tres.

   NO DESAPARECE NINGUNA. Se reordenan por lo que hacen de verdad:

     Pendientes            botón propio, destacado -- es el camino más frecuente
     + Agregar ▾           Del catálogo (era "Nuevo Procedimiento")
                           Desde una promoción (era "Promociones")
     Aplicar plan          botón propio

   POR QUÉ "Aplicar plan" NO ESTÁ EN EL MENÚ: no agrega nada. Aplica el copago
   sobre los procedimientos que YA están en la factura -- por eso su condición
   original exige `procedimientos.length` además de paciente con seguro.
   Meterlo bajo "Agregar" sería mentir sobre lo que hace.

   Las condiciones de habilitado se copian TAL CUAL de FacturaCrear.js.

   ---------------------------------------------------------------------------
   AJUSTE-FRANJA (`unificado`). Yo habia colapsado "Pendientes" y "+ Agregar"
   en un solo boton, razonando que con el panel los dos abren lo mismo. La
   referencia corrige eso y tiene razon: separados, "Pendientes" puede llevar
   SU CONTADOR, y saber que hay 380 pendientes ANTES de abrir nada es
   informacion que el boton unico no daba. El contador sale gratis de la
   precarga, que ya los tiene en memoria.

   ORDEN, de izquierda a derecha:

     [filtro]   [Aplicar plan]   |   [Pendientes 380]   [+ Agregar v]

   · "Aplicar plan" es SECUNDARIO y va separado del grupo de agregar, porque
     MODIFICA lo que ya esta en la factura en vez de traer nada nuevo.
   · Un separador vertical entre los dos grupos.
   · "Pendientes" en azul suave, con su contador.
   · "+ Agregar" es el PRIMARIO y va SIEMPRE el ultimo a la derecha. Con el
     primario en medio, "Aplicar plan" parecia la accion final y no lo es.

   Sin el panel (USAR_PANEL_AGREGAR = false) se pintan los tres controles de
   antes, para que volver atras siga siendo una linea.
   ========================================================================== */

function IconoBuscar() {
    return (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="1.9" strokeLinecap="round" aria-hidden="true">
            <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" />
        </svg>
    );
}

function IconoCheck() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M20 6L9 17l-5-5" />
        </svg>
    );
}

function IconoLista() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="1.9" strokeLinecap="round" aria-hidden="true">
            <path d="M4 7h16" /><path d="M4 12h16" /><path d="M4 17h10" />
        </svg>
    );
}

function IconoChevron() {
    return (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M6 9l6 6 6-6" />
        </svg>
    );
}

export default function CabeceraProcedimientos({
    filtro,
    onFiltro,
    onPendientes,
    onCatalogo,
    onPromociones,
    onPlanes,
    puedePendientes,
    puedeCatalogo,
    puedePromociones,
    puedePlanes,
    // Con el panel unificado, el orden nuevo. Sin él, los tres de antes.
    unificado = false,
    /* Cuántos pendientes tiene el paciente. Lo pasa la pantalla LEYENDO LA
       CACHÉ de la precarga: `null` mientras no se sepa, y entonces no se
       pinta contador. Nunca dispara una petición para saberlo. */
    conteoPendientes = null,
}) {
    const procedimientos = useSelector((state) => state.facturaredux.procedimientos) || [];
    const [menuAbierto, setMenuAbierto] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        if (!menuAbierto) return undefined;
        const alPulsarTecla = (e) => e.key === "Escape" && setMenuAbierto(false);
        const alPulsarFuera = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setMenuAbierto(false);
        };
        document.addEventListener("keydown", alPulsarTecla);
        document.addEventListener("mousedown", alPulsarFuera);
        return () => {
            document.removeEventListener("keydown", alPulsarTecla);
            document.removeEventListener("mousedown", alPulsarFuera);
        };
    }, [menuAbierto]);

    const elegir = (accion) => {
        setMenuAbierto(false);
        accion?.();
    };

    return (
        <div className="fcrear__ch">
            <span className="fcrear__ct">Procedimientos</span>
            {procedimientos.length > 0 && (
                <span className="fcrear__pastilla">{procedimientos.length}</span>
            )}

            <div className="fcrear__ch-fin">
                {/* Filtra lo que YA está en la factura. No busca en el
                    catálogo: eso exigiría cargarlo al abrir la pantalla, y una
                    petición nueva es justo lo que este rediseño no hace. */}
                <label className="fcrear__buscador">
                    <IconoBuscar />
                    <input
                        type="search"
                        value={filtro}
                        onChange={(e) => onFiltro(e.target.value)}
                        placeholder="Filtrar en la factura"
                        aria-label="Filtrar procedimientos de la factura"
                    />
                </label>

                {unificado ? (
                    <>
                        {/* MODIFICA lo que ya está: fuera del grupo de agregar. */}
                        <button
                            type="button"
                            className="fcrear__btn"
                            onClick={onPlanes}
                            disabled={!puedePlanes}
                            title={
                                puedePlanes
                                    ? "Aplicar el copago del plan a los procedimientos"
                                    : "Requiere un paciente con seguro y al menos un procedimiento"
                            }
                            data-testid="fcrear-aplicar-plan"
                        >
                            <IconoCheck /> Aplicar plan
                        </button>

                        <span className="fcrear__separador" aria-hidden="true" />

                        <button
                            type="button"
                            className="fcrear__btn fcrear__btn--destacado"
                            onClick={onPendientes}
                            disabled={!puedePendientes}
                            title={
                                puedePendientes
                                    ? "Procedimientos pendientes del paciente"
                                    : "Selecciona primero un paciente"
                            }
                            data-testid="fcrear-pendientes"
                        >
                            <IconoLista /> Pendientes
                            {conteoPendientes !== null && conteoPendientes > 0 && (
                                <span className="fcrear__btn-contador">{conteoPendientes}</span>
                            )}
                        </button>

                        {/* PRIMARIO, siempre el último a la derecha. */}
                        <div className="fcrear__menu-wrap" ref={menuRef}>
                            <button
                                type="button"
                                className="fcrear__btn fcrear__btn--primario"
                                onClick={() => setMenuAbierto((v) => !v)}
                                disabled={!puedeCatalogo && !puedePromociones}
                                aria-expanded={menuAbierto}
                                aria-haspopup="menu"
                                data-testid="fcrear-agregar"
                            >
                                + Agregar <IconoChevron />
                            </button>

                            {menuAbierto && (
                                <div className="fcrear__menu" role="menu">
                                    <button
                                        type="button"
                                        role="menuitem"
                                        className="fcrear__menu-item"
                                        onClick={() => elegir(onCatalogo)}
                                        disabled={!puedeCatalogo}
                                    >
                                        Del catálogo
                                    </button>
                                    <button
                                        type="button"
                                        role="menuitem"
                                        className="fcrear__menu-item"
                                        onClick={() => elegir(onPromociones)}
                                        disabled={!puedePromociones}
                                    >
                                        Desde una promoción
                                    </button>
                                </div>
                            )}
                        </div>
                    </>
                ) : (
                <>
                <button
                    type="button"
                    className="fcrear__btn fcrear__btn--destacado"
                    onClick={onPendientes}
                    disabled={!puedePendientes}
                    title={puedePendientes ? "Procedimientos pendientes del paciente" : "Selecciona primero un paciente"}
                >
                    Pendientes
                </button>

                <div className="fcrear__menu-wrap" ref={menuRef}>
                    <button
                        type="button"
                        className="fcrear__btn fcrear__btn--primario"
                        onClick={() => setMenuAbierto((v) => !v)}
                        disabled={!puedeCatalogo && !puedePromociones}
                        aria-expanded={menuAbierto}
                        aria-haspopup="menu"
                    >
                        + Agregar <IconoChevron />
                    </button>

                    {menuAbierto && (
                        <div className="fcrear__menu" role="menu">
                            <button
                                type="button"
                                role="menuitem"
                                className="fcrear__menu-item"
                                onClick={() => elegir(onCatalogo)}
                                disabled={!puedeCatalogo}
                            >
                                Del catálogo
                            </button>
                            <button
                                type="button"
                                role="menuitem"
                                className="fcrear__menu-item"
                                onClick={() => elegir(onPromociones)}
                                disabled={!puedePromociones}
                            >
                                Desde una promoción
                            </button>
                        </div>
                    )}
                </div>
                <button
                    type="button"
                    className="fcrear__btn"
                    onClick={onPlanes}
                    disabled={!puedePlanes}
                    title={
                        puedePlanes
                            ? "Aplicar el copago del plan a los procedimientos"
                            : "Requiere un paciente con seguro y al menos un procedimiento"
                    }
                >
                    Aplicar plan
                </button>
                </>
                )}
            </div>
        </div>
    );
}
